/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { difference, differenceWith, isEqual } from 'lodash'

import {
  diffAllCCMClusterRoleAssignments,
  diffSpecificCCMClusterRoleAssignments,
} from '@modules/ccm-user-components/rbac/lib'
import type {
  DeploymentRoleAssignment,
  OrganizationRoleAssignment,
  RoleAssignments,
} from '@modules/cloud-api/v1/types'
import {
  getApplicationRolesForRoleAssignment,
  getDefaultEsKibanaApiAccessValue,
  getEmptyRoleAssignments,
  getOrganizationOwnerApiAccessValue,
} from '@modules/role-assignments-lib'
import diffProjectRoleAssignments from '@modules/role-assignments-lib/diff'
import type { OrganizationRoleId } from '@modules/role-assignments-lib/types'
import { ROLE_IDS } from '@modules/role-assignments-lib/types'
import type { ApiAccessOptions } from '@modules/role-assignments-components/ApiAccessSelector/types'

export const toggleOrganizationBillingRoleAssignment = (
  roleAssignments: RoleAssignments,
  organizationId: string,
): RoleAssignments => {
  // shouldReplace must be true when ORGANIZATION_BILLING doesn't exist
  const shouldReplace =
    roleAssignments.organization?.every(({ role_id }) => role_id !== 'billing-admin') ?? true

  // merge with existing roleAssignments since ORGANIZATION_BILLING doesn't
  // interfere with deployment roles assignments
  return {
    ...roleAssignments,
    organization: shouldReplace
      ? [
          {
            organization_id: organizationId,
            role_id: 'billing-admin',
          },
        ]
      : [],
  }
}

export const toggleOrganizationOwnerRoleAssignment = (
  roleAssignments: RoleAssignments,
  organizationId: string,
  isApiKey?: boolean,
  apiAccessValue?: ApiAccessOptions,
  toggleOverride?: boolean,
): RoleAssignments => {
  // shouldReplace must be true when organization-admin doesn't exist
  const shouldReplace =
    toggleOverride ??
    roleAssignments.organization?.every(({ role_id }) => role_id !== 'organization-admin') ??
    true

  const applicationRoles = getApplicationRolesForRoleAssignment('admin', isApiKey, apiAccessValue)

  // do not merge with existing roleAssignments since organization owner
  // replaces everything else, including deployment roles assignments
  return {
    ...getEmptyRoleAssignments(),
    organization: shouldReplace
      ? [
          {
            organization_id: organizationId,
            role_id: 'organization-admin',
            application_roles: applicationRoles,
          },
        ]
      : [],
  }
}

export const filterDeploymentRoleAssignmentByAll = (
  deploymentRoleAssignments: DeploymentRoleAssignment[] | undefined,
  all: boolean,
): DeploymentRoleAssignment[] =>
  deploymentRoleAssignments
    ?.filter((deploymentRoleAssignment) => (deploymentRoleAssignment.all ?? false) === all)
    .filter(hasValidDeploymentRoleId)
    .sort(sortDeploymentRoleAssignments) || []

const hasValidDeploymentRoleId = (
  roleAssignment: DeploymentRoleAssignment,
): roleAssignment is DeploymentRoleAssignment => {
  const roleIds: readonly string[] = ROLE_IDS.deployment
  return roleIds.includes(roleAssignment.role_id)
}

export const isOrganizationRoleSelected = (
  roleAssignments: RoleAssignments,
  role: OrganizationRoleId,
): boolean =>
  roleAssignments.organization?.some((organization) => organization.role_id === role) ?? false

const diffOrganizationRoleAssignments = (
  original: OrganizationRoleAssignment[] = [],
  changed: OrganizationRoleAssignment[] = [],
): {
  added: OrganizationRoleAssignment[]
  removed: OrganizationRoleAssignment[]
} => ({
  added: differenceWith(changed, original, isEqual),
  removed: differenceWith(original, changed, isEqual),
})

type DeploymentRoleChange = {
  added: DeploymentRoleAssignment[]
  removed: DeploymentRoleAssignment[]
}

const diffAllDeploymentsRoleAssignments = (
  original: DeploymentRoleAssignment[] = [],
  changed: DeploymentRoleAssignment[] = [],
): DeploymentRoleChange => {
  const originalAllDeploymentsRoleAssignments: DeploymentRoleAssignment[] =
    filterDeploymentRoleAssignmentByAll(original, true)

  const changedAllDeploymentsRoleAssignments: DeploymentRoleAssignment[] =
    filterDeploymentRoleAssignmentByAll(changed, true)

  return {
    added: differenceWith(
      changedAllDeploymentsRoleAssignments,
      originalAllDeploymentsRoleAssignments,
      isEqual,
    ),
    removed: differenceWith(
      originalAllDeploymentsRoleAssignments,
      changedAllDeploymentsRoleAssignments,
      isEqual,
    ),
  }
}

const diffSpecificDeploymentsRoleAssignments = (
  original: DeploymentRoleAssignment[] = [],
  changed: DeploymentRoleAssignment[] = [],
): DeploymentRoleChange => {
  const originalSpecificDeploymentsRoleAssignments = filterDeploymentRoleAssignmentByAll(
    original,
    false,
  )

  const originalDeploymentIdsByRoleId = getDeploymentIdsByRoleId(
    originalSpecificDeploymentsRoleAssignments,
  )

  const changedSpecificDeploymentsRoleAssignments = filterDeploymentRoleAssignmentByAll(
    changed,
    false,
  )

  const changedDeploymentIdsByRoleId = getDeploymentIdsByRoleId(
    changedSpecificDeploymentsRoleAssignments,
  )

  const added = diffDeploymentIds(
    changedSpecificDeploymentsRoleAssignments,
    originalDeploymentIdsByRoleId,
  ).filter(({ deployment_ids = [] }) => deployment_ids.length > 0)

  const removed = diffDeploymentIds(
    originalSpecificDeploymentsRoleAssignments,
    changedDeploymentIdsByRoleId,
  ).filter(({ deployment_ids = [] }) => deployment_ids.length > 0)

  return {
    added,
    removed,
  }

  function getDeploymentIdsByRoleId(roleAssignments: DeploymentRoleAssignment[]): {
    [roleId: string]: string[]
  } {
    return roleAssignments.reduce(
      (prev, { role_id: roleId, deployment_ids: deploymentIds = [] }) => ({
        ...prev,
        [roleId]: (prev[roleId] ?? []).concat(deploymentIds),
      }),
      {},
    )
  }

  function diffDeploymentIds(
    roleAssignments: DeploymentRoleAssignment[],
    deploymentIdsByRoleId: { [roleId: string]: string[] },
  ): DeploymentRoleAssignment[] {
    return roleAssignments.map((roleAssignment) => ({
      ...roleAssignment,
      deployment_ids: difference(
        roleAssignment.deployment_ids,
        deploymentIdsByRoleId[roleAssignment.role_id] ?? [],
      ),
    }))
  }
}

export type RoleAssignmentsDiff = {
  added: RoleAssignments
  removed: RoleAssignments
}

export const diffRoleAssignments = (
  original: RoleAssignments,
  changed: RoleAssignments,
  isApiKey?: boolean,
): RoleAssignmentsDiff => {
  const organizationRoleAssignmentsDiff = diffOrganizationRoleAssignments(
    original.organization,
    changed.organization,
  )

  const allDeploymentsRoleAssignmentsDiff = diffAllDeploymentsRoleAssignments(
    original.deployment,
    changed.deployment,
  )

  const specificDeploymentsRoleAssignmentsDiff = diffSpecificDeploymentsRoleAssignments(
    original.deployment,
    changed.deployment,
  )

  const projectRoleAssignmentsDiff = diffProjectRoleAssignments(
    original.project,
    changed.project,
    isApiKey,
  )

  const allCCMClusterRoleAssignmentsDiff = diffAllCCMClusterRoleAssignments(
    original.cloud_connected_resource,
    changed.cloud_connected_resource,
  )

  const specificCCMClusterRoleAssignmentsDiff = diffSpecificCCMClusterRoleAssignments(
    original.cloud_connected_resource,
    changed.cloud_connected_resource,
  )

  return {
    added: {
      organization: organizationRoleAssignmentsDiff.added,
      deployment: allDeploymentsRoleAssignmentsDiff.added.concat(
        specificDeploymentsRoleAssignmentsDiff.added,
      ),
      project: projectRoleAssignmentsDiff.added,
      cloud_connected_resource: allCCMClusterRoleAssignmentsDiff.added.concat(
        specificCCMClusterRoleAssignmentsDiff.added,
      ),
    },
    removed: {
      organization: organizationRoleAssignmentsDiff.removed,
      deployment: allDeploymentsRoleAssignmentsDiff.removed.concat(
        specificDeploymentsRoleAssignmentsDiff.removed,
      ),
      project: projectRoleAssignmentsDiff.removed,
      cloud_connected_resource: allCCMClusterRoleAssignmentsDiff.removed.concat(
        specificCCMClusterRoleAssignmentsDiff.removed,
      ),
    },
  }
}

const deploymentRolesOrder = {
  ['deployment-admin']: 1,
  ['deployment-editor']: 2,
  ['deployment-viewer']: 3,
}

const sortDeploymentRoleAssignments = (
  deploymentRoleAssignment1: DeploymentRoleAssignment,
  deploymentRoleAssignment2: DeploymentRoleAssignment,
): number =>
  deploymentRolesOrder[deploymentRoleAssignment1.role_id] -
  deploymentRolesOrder[deploymentRoleAssignment2.role_id]

export const getOrganizationOwnerApiAccess = (
  roleAssignments: RoleAssignments,
  isApiKey: boolean,
): ApiAccessOptions => {
  const organizationOwnerRole = roleAssignments.organization?.find(
    (role) => role.role_id === 'organization-admin',
  )

  if (organizationOwnerRole) {
    return getOrganizationOwnerApiAccessValue(organizationOwnerRole, isApiKey)
  }

  return getDefaultEsKibanaApiAccessValue(isApiKey)
}
